import type { I18n } from '@learnforge/i18n';

export interface CreateSliderRowOptions {
  id: string;
  labelKey?: string;
  label?: string;
  i18n: I18n<string>;
  min: number;
  max: number;
  step: number;
  value: number;
  format?: (v: number) => string;
  onChange: (v: number) => void;
}

export interface SliderRowHandle {
  element: HTMLElement;
  /** The range input. */
  input: HTMLInputElement;
  /** The editable numeric readout (text input; decimal keyboard on phones). */
  valueInput: HTMLInputElement;
  setValue(v: number): void;
  getValue(): number;
}

export function createSliderRow(opts: CreateSliderRowOptions): SliderRowHandle {
  if (opts.label === undefined && opts.labelKey === undefined) {
    throw new Error('createSliderRow: provide label or labelKey');
  }

  const format = opts.format ?? ((v: number) => v.toFixed(1));

  const sliderId = `dim_${opts.id}`;
  const row = document.createElement('div');
  row.className = 'dim-row';
  row.innerHTML = `
    <label class="dim-label" for="${sliderId}"></label>
    <input type="range" class="dim-slider" id="${sliderId}" />
    <input type="text" class="dim-value" inputmode="decimal" autocomplete="off" spellcheck="false" />
  `;
  const labelEl = row.querySelector('.dim-label') as HTMLLabelElement;
  const slider = row.querySelector('input[type="range"]') as HTMLInputElement;
  const valueEl = row.querySelector('input.dim-value') as HTMLInputElement;

  slider.min = String(opts.min);
  slider.max = String(opts.max);
  // Continuous thumb: `step="any"` lets the handle glide to any sub-step position
  // instead of snapping tick-by-tick. The declared `opts.step` still defines the
  // *value* grid via `readValue` below — it just no longer constrains the thumb.
  slider.step = 'any';
  slider.value = String(opts.value);

  const clamp = (v: number): number => Math.min(opts.max, Math.max(opts.min, v));

  // Integer-grid steps drive discrete params (counts, indices, coarse whole units):
  // snap the emitted value to that grid so consumers that index/loop on it never
  // receive a fraction. Sub-unit steps are continuous quantities — emit the raw
  // value so the control is genuinely continuous, not re-quantised to `opts.step`.
  const readValue = (raw: number): number => {
    if (Number.isInteger(opts.step) && opts.step >= 1) {
      const snapped = opts.min + Math.round((raw - opts.min) / opts.step) * opts.step;
      return clamp(snapped);
    }
    return raw;
  };

  let current = opts.value;
  const labelText = (): string => opts.label ?? opts.i18n.t(opts.labelKey!);
  labelEl.textContent = labelText();
  valueEl.setAttribute('aria-label', labelText());

  const showFormatted = (): void => {
    valueEl.value = format(current);
  };
  showFormatted();

  slider.addEventListener('input', () => {
    current = readValue(parseFloat(slider.value));
    showFormatted();
    opts.onChange(current);
  });

  // ---- Typed entry: the readout is an input. Focus shows the raw number; Enter /
  // blur commit (clamped + grid-snapped like a drag); Escape reverts. `dirty`
  // tracks whether the user typed since focus so a blur after Enter/Escape is a
  // no-op instead of re-parsing the formatted text (which may carry a unit). ----
  let dirty = false;
  const applyTyped = (v: number): void => {
    const next = readValue(clamp(v));
    const changed = next !== current;
    current = next;
    slider.value = String(next);
    showFormatted();
    if (changed) opts.onChange(next);
  };
  const commitText = (): void => {
    dirty = false;
    const raw = valueEl.value.trim().replace(',', '.');
    const parsed = raw === '' ? NaN : Number(raw);
    if (!Number.isFinite(parsed)) {
      showFormatted();
      return;
    }
    applyTyped(parsed);
  };
  valueEl.addEventListener('focus', () => {
    valueEl.value = String(current);
    dirty = false;
    valueEl.select();
  });
  valueEl.addEventListener('input', () => {
    dirty = true;
  });
  valueEl.addEventListener('keydown', (ev) => {
    if (ev.key === 'Enter') {
      ev.preventDefault();
      commitText();
      valueEl.blur();
    } else if (ev.key === 'Escape') {
      ev.preventDefault();
      dirty = false;
      showFormatted();
      valueEl.blur();
    } else if (ev.key === 'ArrowUp' || ev.key === 'ArrowDown') {
      ev.preventDefault();
      const dir = ev.key === 'ArrowUp' ? 1 : -1;
      applyTyped(Number((current + dir * opts.step).toFixed(6)));
      valueEl.value = String(current);
      dirty = false;
    }
  });
  valueEl.addEventListener('blur', () => {
    if (dirty) commitText();
    else showFormatted();
  });

  if (opts.labelKey !== undefined) {
    opts.i18n.subscribe(() => {
      labelEl.textContent = labelText();
      valueEl.setAttribute('aria-label', labelText());
    });
  }

  return {
    element: row,
    input: slider,
    valueInput: valueEl,
    setValue(v) {
      current = v;
      slider.value = String(v);
      if (document.activeElement !== valueEl) showFormatted();
    },
    getValue() {
      return current;
    },
  };
}
